type AppEnvironment = "development" | "test" | "staging" | "production";
type DataMode = "mock" | "api";

const enumValue = <T extends string>(
  name: string,
  value: string | undefined,
  allowed: readonly T[],
  fallback: T,
): T => {
  const resolved = value || fallback;
  if (!allowed.includes(resolved as T)) {
    throw new Error(`${name} must be one of: ${allowed.join(", ")}`);
  }
  return resolved as T;
};

const optionalUrl = (name: string, value: string | undefined) => {
  if (!value) return undefined;
  try {
    return new URL(value).toString();
  } catch {
    throw new Error(`${name} must be a valid absolute URL`);
  }
};

const environment = enumValue<AppEnvironment>(
  "VITE_APP_ENV",
  import.meta.env.VITE_APP_ENV,
  ["development", "test", "staging", "production"],
  "development",
);
const dataMode = enumValue<DataMode>(
  "VITE_DATA_MODE",
  import.meta.env.VITE_DATA_MODE,
  ["mock", "api"],
  "mock",
);
const apiUrl = optionalUrl("VITE_API_URL", import.meta.env.VITE_API_URL);
const neonAuthUrl = optionalUrl("VITE_NEON_AUTH_URL", import.meta.env.VITE_NEON_AUTH_URL);

if (dataMode === "api") {
  const missing = [
    !apiUrl && "VITE_API_URL",
    !neonAuthUrl && "VITE_NEON_AUTH_URL",
  ].filter(Boolean);
  if (missing.length) {
    throw new Error(
      `Invalid frontend environment: ${missing.join(", ")} required in API mode`,
    );
  }
}

export const appEnv = Object.freeze({
  environment,
  dataMode,
  apiUrl,
  neonAuthUrl,
  /** Mock authentication is allowed for development/test/staging, never production. */
  demoAuthEnabled: dataMode === "mock" && environment !== "production",
});

export type { AppEnvironment, DataMode };
